import React, { useRef, useState } from 'react';
import Immutable from 'immutable';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  Container,
  Image,
  Button,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  CoreLink,
  Node,
  Paragraph,
  Taxonomy,
} from '@uncinc/react-drupal-core';

import { WysiwygField, Share, SearchItem } from '../../../../components';
import Carousel from '../../../../components/Carousel';
import { ChipFacet } from '../../../../components/SearchKit/ChipFacet';
import { SearchFacets } from '../../../../components/SearchKit/SearchFacets';
import { SelectFacet } from '../../../../components/SearchKit/SelectFacet';
import NewsletterBlock from '../../../../components/NewsletterBlock';
import * as query from './query.graphql';
import './style.scss';
import SearchLoadMore from '../../../../components/SearchKit/SearchLoadMore';
import SearchKitVNL from '../../../../components/SearchKit';
import SearchResults from '../../../../components/SearchKit/SearchResults';
import SwirlIllustrations from '../../../../components/SwirlIllustrations';
import {
  SCREEN_MOBILE, SCREEN_NORMAL, SCREEN_TABLET,
  TAXONOMY_LOCATION,
  TAXONOMY_STAGEOFLIFE,
  TAXONOMY_SUBJECT,
} from '../../../../config/constants';

const SearchHeader = () => {
  return (
    <span />
  );
};

const TaxonomySubject = ({ entity }) => {
  const { t } = useTranslation();

  const vocabulary = entity.getIn(['vid', 0, 'target_id']);
  const subjectName = entity.getIn(['name', 0, 'value'], '');
  const vocabularyTitle = entity.getIn(['vid', 0, 'target_id'], '');
  const relatedSubjects = entity.getIn(['field_related_subjects'], Immutable.Map());
  const expertsTitle = entity.getIn(['field_experts_title', 0, 'value'], t('expert.node_title'));
  const cooperationTitle = entity.getIn(['field_cooperation_title', 0, 'value'], '');
  const [activeSubTypeFilterClassName, setActiveSubTypeFilterClassName] = useState('');

  // Toggles the Sub-type filter based on the content-type chosen
  const toggleSubTypeFilter = (facet, filters) => {
    const typeFilter = filters.filter((e) => { return e.identifier === 'type'; });
    let newFilterClassValue = '';
    if (typeFilter.length > 0) {
      newFilterClassValue = `field_${typeFilter[0].value}_type_name-active`;
    }
    setActiveSubTypeFilterClassName(newFilterClassValue);
  };

  const taxonomyFilter = (vocabularyName) => {
    switch (vocabularyName) {
      case 'stage_of_life':
        return 'field_phase_of_life_name';
      case 'locatie':
        return 'field_locations_name';
      default:
        return `field_${vocabularyName}_name`;
    }
  };

  const contentRef = useRef(null);
  const scrollToContent = () => contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const filters = [
    {
      identifier: taxonomyFilter(vocabulary),
      value: subjectName,
    },
    {
      identifier: 'field_show_in_dossier',
      value: 'true',
    },
  ];

  let relatedContentTitle = 'Alle informatie';

  if (vocabulary === TAXONOMY_SUBJECT) {
    relatedContentTitle = `Alle informatie over ${subjectName}`;
  } else if (vocabulary === TAXONOMY_STAGEOFLIFE || vocabulary === TAXONOMY_LOCATION) {
    relatedContentTitle = `Alle informatie voor ${subjectName}`;
  }

  return (
    <div className={`TaxonomySubject TaxonomySubject--${vocabulary} ${activeSubTypeFilterClassName}`}>
      <Container>
        <div className="TaxonomySubject__hero">
          {entity.getIn(['field_media_image', 0]) && (
            <Image
              lazyload={false}
              className="TaxonomySubject__hero__image"
              includeWebP
              src={{
                [SCREEN_MOBILE]: entity.getIn(['field_media_image', 0, 'styles', 'hero_mobile']),
                [SCREEN_TABLET]: entity.getIn(['field_media_image', 0, 'styles', 'hero_tablet']),
                [SCREEN_NORMAL]: entity.getIn(['field_media_image', 0, 'styles', 'hero_desktop']),
              }}
              alt={entity.getIn(['field_media_image', 0, 'alt'], '')}
            />
          )}

          <SwirlIllustrations
            variant="hero"
          />
        </div>
        <div className="row">
          <div className="col-12 col-lg-9">
            <div className="TaxonomySubject__intro" id="main">
              <div className="TaxonomySubject__headerWrapper">
                <div className="TaxonomySubject__type">{t(`vocabularies.${vocabularyTitle}`)}</div>
                <h1>{entity.getIn(['name', 0, 'value'], '')}</h1>
                <Button onClick={scrollToContent} className="Button--ghost">{t('general.to_all_info')} <i className="icon icon-arrow-right" /></Button>
              </div>
              {entity.getIn(['description', 0, 'processed']) ? (
                <WysiwygField
                  property="schema:text"
                  className="TaxonomySubject__body subheading2"
                  html={entity.getIn(['description', 0, 'processed'], '')}
                />
              ) : null}

              {vocabulary !== 'subject' && relatedSubjects.size > 0 && (
                <div className="TaxonomySubject__relevant">
                  {/* title */}
                  <h2 className="TaxonomySubject__relevant__title asH3">Relevante onderwerpen voor <strong>{subjectName}</strong></h2>
                  <div className="TaxonomySubject__relevant__buttons_container">

                    {relatedSubjects.map((subj) => {
                      const name = subj.getIn(['entity', 'name', 0, 'value']);
                      const uuid = subj.getIn(['entity', 'uuid', 0, 'value']);
                      const imageUrl = subj.getIn(['entity', 'field_media_image', 0, 'styles', '1_1']);
                      const imageAlt = subj.getIn(['entity', 'field_media_image', 0, 'alt'], '');
                      const dossierTitle = subj.getIn(['entity', 'vid', 0, 'target_id']);
                      const subjectUrl = subj.getIn(['entity', 'paths', 'nl']);

                      return (
                        <CoreLink to={subjectUrl} key={uuid}>
                          <div className="TaxonomySubject__relevant__button">
                            {/* image */}
                            <div className="TaxonomySubject__relevant__button_image_container">
                              {imageUrl && (
                                <Image
                                  className="TaxonomySubject__relevant__button_image"
                                  src={imageUrl}
                                  alt={imageAlt}
                                />
                              )}
                            </div>
                            {/* type and description */}
                            <div className="TaxonomySubject__relevant__button_text">
                              <p>{t(`vocabularies.${dossierTitle}`)}</p>
                              <h3 className="asH5">{name}</h3>
                            </div>
                            {/* icon */}
                            <i className="icon icon-arrow-right" />
                          </div>
                        </CoreLink>
                      );
                    })}
                  </div>
                </div>
              )}

              <Button onClick={scrollToContent} className="Button--ghost" href="#content">{t('general.to_all_info')} <i className="icon icon-arrow-right" /></Button>

              {
                [
                  'field_paragraph_urgency',
                  'field_paragraph_linkthrough',
                  'field_paragraph_text',
                  'field_paragraph_perspective',
                  'field_paragraph_quote',
                ].map((field) => {
                  if (entity.get(field)) {
                    const uuid = entity.getIn(['uuid', 0, 'value']);
                    return (
                      <div className="TaxonomySubject__intro__paragraphs" key={uuid}>
                        {entity.get(field, Immutable.List()).map((paragraph) => {
                          const id = paragraph.get('target_id');
                          const language = paragraph.getIn(['entity', 'langcode', 0, 'value']);
                          return (
                            <Paragraph
                              key={`paragraph:${id}:${language}`}
                              id={id}
                              entity={paragraph.get('entity')}
                            />
                          );
                        })}
                      </div>
                    );
                  }
                })
              }

            </div>
          </div>
          <div className="col-12 col-lg-3">
            <div className="TaxonomySubject__sidebar">
              {entity.get('field_experts') ? (
                <div className="TaxonomySubject__sidebar__experts">
                  <h2 className="TaxonomySubject__sidebar__experts__title">{expertsTitle}</h2>
                  {entity.get('field_experts', Immutable.List()).map((expert) => (
                    <Node
                      viewMode="teaser"
                      key={expert.get('target_uuid')}
                      id={expert.getIn(['entity', 'nid', 0, 'value'])}
                      entity={expert.get('entity')}
                    />
                  ))}
                </div>
              ) : null}
              {entity.get('field_partners', Immutable.List()).size > 0 ? (
                <div className="TaxonomySubject__sidebar__partners">
                  {cooperationTitle && <h2 className="TaxonomySubject__sidebar__partners__title">{cooperationTitle}</h2>}
                  {entity.get('field_partners', Immutable.List()).map((partner) => (
                    <Node
                      viewMode="teaser"
                      key={partner.get('target_uuid')}
                      id={partner.getIn(['entity', 'nid', 0, 'value'])}
                      entity={partner.get('entity')}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Container>
      <div ref={contentRef} className="TaxonomySubject__related_content">
        <Container>
          <h2>
            {relatedContentTitle}
          </h2>

          {Object.keys(query).length > 0 && (
            <SearchKitVNL
              key={JSON.stringify(filters)}
              query={query}
              defaultSorting="created"
              defaultFilters={filters}
              pageSize={15}
            >
              {(data, loading) => data && (
                <>
                  <div className="Search__results__filters">
                    <div className="Search__results__filters__items">
                      <span>Filter</span>
                      <SearchFacets data={data.dossier} loading={loading} facets={[ChipFacet, SelectFacet]} onChange={toggleSubTypeFilter} />
                      <div className="total-count minimized">
                        {data.dossier.results.summary.total} {t('general.search_results')}
                      </div>
                    </div>
                  </div>
                  <SearchResults
                    data={data.dossier}
                    renderItem={SearchItem}
                    renderHeader={SearchHeader}
                    appendResults
                  />
                  {data && (data.dossier.results.hits.page.pageNumber < data.dossier.results.hits.page.totalPages) && (
                    <SearchLoadMore data={data.dossier} />
                  )}
                </>
              )}
            </SearchKitVNL>
          )}
        </Container>
      </div>
      {entity.get('field_paragrafen') ? (
        <div className="TaxonomySubject__paragraphs">
          {entity.get('field_paragrafen', Immutable.List()).map((paragraph) => {
            const id = paragraph.get('target_id');
            const language = paragraph.getIn(['entity', 'langcode', 0, 'value']);
            return (
              <Paragraph
                key={`paragraph:${id}:${language}`}
                id={id}
                entity={paragraph.get('entity')}
              />
            );
          })}
        </div>
      ) : null}
      {entity.get('field_related_dossiers') ? (
        <div className="TaxonomySubject__related_dossiers">
          <Container>
            <h2>{t('get_started.title')}</h2>
            <div className="DossiersRelated">
              <Carousel>
                {entity.get('field_related_dossiers', Immutable.List()).map((dossier) => {
                  const id = dossier.getIn(['entity', 'tid', '0', 'value']);
                  const uuid = dossier.getIn(['entity', 'uuid', 0, 'value']);
                  const langcode = dossier.getIn(['entity', 'langcode', 0, 'value']);
                  const classes = classNames('DossiersRelated__row');

                  return (
                    <div key={`${uuid}:${langcode}`} className={classes}>
                      <Taxonomy
                        id={id}
                        entity={dossier.get('entity')}
                        viewMode="teaser"
                      />
                    </div>
                  );
                })}
              </Carousel>
            </div>

          </Container>
        </div>
      ) : null}
      {entity.get('field_paragraph_feedback') ? (
        <div className="TaxonomySubject__feedback">
          <Container>
            {entity.get('field_paragraph_feedback', Immutable.List()).map((paragraph) => {
              const id = paragraph.get('target_id');
              const language = paragraph.getIn(['entity', 'langcode', 0, 'value']);
              return (
                <Paragraph
                  key={`paragraph:${id}:${language}`}
                  id={id}
                  entity={paragraph.get('entity')}
                />
              );
            })}
          </Container>
        </div>
      ) : null}
      {entity.get('field_theme', Immutable.List()).size > 0 && (
        <Container>
          <div className="row">
            {entity.get('field_theme').map((theme) => (
              <div className="col-12" key={theme.get('target_uuid')}>
                <Taxonomy
                  id={theme.get('target_id')}
                  entity={theme.get('entity')}
                  viewMode="card"
                />
              </div>
            ))}
          </div>
        </Container>
      )}
      <Container>
        <div className="row">
          <div className="col-12 col-lg-8 offset-lg-2">
            <Share />
          </div>
        </div>
      </Container>

      {/* Newsletter subscription block */}
      <NewsletterBlock />
    </div>
  );
};

export default TaxonomySubject;
